import React, { useEffect, useState, useMemo } from 'react';
import { FormikHelpers, FormikValues } from 'formik';

import { Grid, Stack, Typography, Checkbox, FormGroup, FormControlLabel } from '@mui/material';
import styled from 'styled-components';
import {
	Button,
	InputAutocomplete,
	MiniLoading,
	palette,
	StylovyzeComposableDialog,
	StylovyzeFormV2,
	Text,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { ErrorOutline, Help, EditOutlined as EditIcon } from '@mui/icons-material';
import { getAssetSchema } from '@innovyze/lib_asset_schema';
import { DarkInfoTooltip } from '@Components/deteriorationModel/configurationTab/DarkInfoTooltip';
import { WeibullFallbackBanner } from '@Components/deteriorationModel/utils/WeibullFallbackBanner';
import { WEAVE_FONT } from '@Utils/fonts';

import { useGlobalization } from '@Translations';
import {
	selectAllFailureDefinitions,
	selectDeteriorationModel,
	selectFailureDefinition,
	selectGroupSet,
} from '@Selectors';
import { hasAnyGroupWeibullFallback } from '@Services/deteriorationModels.services';
import { FailureDefinition, WeibullFormulation } from '@Types';

interface DeteriorationModelConfigurationCardProps {
	onUpdateFailureDefinition: (newFailureDefinitionId: string) => Promise<void>;
}

const StyledGridItem = styled(Grid)(({ theme }) => ({
	borderBottom: '1px solid #eef0f3',
	// no left padding so the label column lines up with the title; vertical padding gives row rhythm
	padding: theme.spacing(1, 1, 1, 0),
	// drop the divider under the last row (two cells) so there's no dangling bottom line
	'&:nth-last-of-type(-n + 2)': {
		borderBottom: 'none',
	},
}));

// Label column cell for the Configuration grid: a greyed label plus an info tooltip.
// Returns a single StyledGridItem so the grid's :nth-last-of-type divider rule still holds.
const ConfigLabelCell = ({ label, heading, body }: { label: string; heading: string; body: string }): JSX.Element => (
	<StyledGridItem item xs={2}>
		<Stack direction="row" spacing={1} alignItems="center">
			<Text style={{ color: '#6b7280' }}>{label}</Text>
			<DarkInfoTooltip heading={heading} body={body}>
				<Help sx={{ fontSize: 'small', color: palette.secondary.main }} />
			</DarkInfoTooltip>
		</Stack>
	</StyledGridItem>
);

export const DeteriorationModelConfigurationCard = ({
	onUpdateFailureDefinition,
}: DeteriorationModelConfigurationCardProps) => {
	const { t } = useGlobalization();

	const [isOpen, setOpen] = useState(false);
	const [isCheckboxConfirmed, setCheckboxConfirmed] = useState(false);

	const deteriorationModel = selectDeteriorationModel();
	const model = deteriorationModel.deteriorationModel;

	const isWeibull3ParamEnabled = useIsFeatureEnabled('info360-weibull-3-param');
	const [anyGroupFellBack, setAnyGroupFellBack] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const runId = model?.latestRunResultsId;
		if (
			!isWeibull3ParamEnabled ||
			model?.weibullFormulation !== WeibullFormulation.THREE_PARAMETER ||
			!runId ||
			!model?.id
		) {
			setAnyGroupFellBack(false);
			return;
		}
		hasAnyGroupWeibullFallback(model.id, runId)
			.then(result => {
				if (!cancelled) setAnyGroupFellBack(result);
			})
			.catch(() => {
				if (!cancelled) setAnyGroupFellBack(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isWeibull3ParamEnabled, model?.id, model?.weibullFormulation, model?.latestRunResultsId]);

	const failureDefinitionId = deteriorationModel.deteriorationModel?.failureDefinitionId;
	const failureDefinition = selectFailureDefinition();
	const failureDefinitions = selectAllFailureDefinitions();

	const groupSet = selectGroupSet();

	// Filter failure definitions to match the group set's system and asset type
	const [filteredFailureDefinitions, setFilteredFailureDefinitions] = useState<FailureDefinition[]>([]);
	useEffect(() => {
		const filterFailureDefinitions = async () => {
			const selectedGroupSet = groupSet?.groupSet;
			if (!selectedGroupSet) {
				setFilteredFailureDefinitions([]);
				return;
			}

			const selectedGroupSetAssetSchema = await getAssetSchema({
				systemTypeId: selectedGroupSet.systemType,
				assetTypeId: selectedGroupSet.assetType,
			});
			if (!selectedGroupSetAssetSchema) {
				// TODO: Asset group sets are not following lib yet, so we return all failure definitions for now
				setFilteredFailureDefinitions(failureDefinitions);
				return;
			}

			const filtered = [];
			for (const fd of failureDefinitions) {
				const fdAssetSchema = await getAssetSchema({
					systemTypeId: fd.systemType,
					assetTypeId: fd.assetType,
				});
				if (fdAssetSchema?.id === selectedGroupSetAssetSchema.id) {
					filtered.push(fd);
				}
			}
			setFilteredFailureDefinitions(filtered);
		};

		filterFailureDefinitions();
	}, [groupSet, failureDefinitions]);

	const availableFailureDefinitions = useMemo(() => {
		return filteredFailureDefinitions.map(fd => ({
			key: fd.id,
			value: fd.name,
		}));
	}, [filteredFailureDefinitions]);

	const handleClose = () => {
		setOpen(false);
		setCheckboxConfirmed(false);
	};

	const handleSubmit = async (values: FormikValues, { setSubmitting }: FormikHelpers<FormikValues>) => {
		setSubmitting(true);
		try {
			await onUpdateFailureDefinition(values.failureDefinition);
			handleClose();
			setSubmitting(false);
		} catch (e) {
			setSubmitting(false);
		}
	};

	return (
		<>
			<StylovyzeFormV2
				initialValues={{
					failureDefinition: failureDefinition.failureDefinition?.id,
				}}
				validate={values => {
					const errors: { [key: string]: string } = {};
					if (!values.failureDefinition) {
						errors.failureDefinition = 'Required';
					}
					return errors;
				}}
				onSubmit={handleSubmit}>
				{({ submitForm, isSubmitting, errors }) => {
					return (
						<StylovyzeComposableDialog
							onCancel={handleClose}
							open={isOpen}
							title={t('Change Failure Definition')}
							footer={
								<>
									<Button variant="contained" color="secondary" onClick={handleClose}>
										{t('Cancel')}
									</Button>
									<Button
										variant="contained"
										color="primary"
										disabled={isSubmitting || !isCheckboxConfirmed}
										onClick={submitForm}>
										{t('Update')}
									</Button>
								</>
							}>
							<InputAutocomplete
								options={availableFailureDefinitions}
								fieldAttrs={{ name: 'failureDefinition' }}
								label={t('Failure Definition')}
								textFieldProps={{
									fullWidth: true,
								}}
								error={!!errors.failureDefinition}
								helperText={errors.failureDefinition ? errors.failureDefinition.toString() : ''}
							/>
							<Stack spacing={2} direction="column" sx={{ mt: 2 }}>
								<Stack direction="row" spacing={1} alignItems="flex-start">
									<ErrorOutline sx={{ mt: '2px' }} />
									<Text>
										{t(
											'Applying a new Failure Definition will update failures for all groups; groups with insufficient failure records (≤5 records) will be set to Undefined; if you have changed or added survival curves for any groups, these changes will be lost.',
										)}
									</Text>
								</Stack>
								<FormGroup>
									<FormControlLabel
										control={
											<Checkbox
												checked={isCheckboxConfirmed}
												onChange={e => setCheckboxConfirmed(e.target.checked)}
												size="small"
												color="default"
											/>
										}
										label={
											<Text>
												{t('I understand that failures for all groups will be updated and any curve changes lost.')}
											</Text>
										}
									/>
								</FormGroup>
							</Stack>
						</StylovyzeComposableDialog>
					);
				}}
			</StylovyzeFormV2>
			<Stack
				spacing={1.5}
				sx={{
					mt: 2,
					px: 3,
					py: 2.5,
					backgroundColor: '#ffffff',
					border: '1px solid #e5e7eb',
					borderRadius: '12px',
					'& .MuiTypography-root': { fontFamily: WEAVE_FONT },
				}}>
				{isWeibull3ParamEnabled && anyGroupFellBack && (
					<WeibullFallbackBanner
						message={t(
							// eslint-disable-next-line quotes
							"This model's 3-parameter Weibull selection ran as 2-parameter for one or more asset groups. See Results for details.",
						)}
					/>
				)}
				<Stack spacing={0.5}>
					<Typography
						sx={{ fontFamily: WEAVE_FONT, fontSize: '1.25rem', fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>
						{t('Configuration')}
					</Typography>
					<Typography sx={{ fontFamily: WEAVE_FONT, fontSize: '0.8125rem', color: '#6b7280' }}>
						{t('Settings this model runs with.')}
					</Typography>
				</Stack>
				<Grid container sx={{ justifyContent: 'flex-start' }} direction="row">
					<ConfigLabelCell
						label={t('Asset Group Set:')}
						heading={t('Asset Group Set')}
						body={t(
							'This is the asset group set your model will be run for. All the groups in this set are listed below.',
						)}
					/>
					<StyledGridItem item xs={10}>
						<MiniLoading loading={groupSet?.isLoading ?? false}>
							<Text style={{ fontWeight: 600 }}>{groupSet?.groupSet?.name ?? ''}</Text>
						</MiniLoading>
					</StyledGridItem>
					<ConfigLabelCell
						label={t('Failure Definition:')}
						heading={t('Failure Definition')}
						body={t(
							'If you have selected a failure definition, your failure data will be used to generate curves for the asset groups that have this selected as the curve method. If there are ≤5 failures for a group, you will need to select another curve method.',
						)}
					/>
					<StyledGridItem item xs={10}>
						<MiniLoading loading={failureDefinitionId ? failureDefinition.inProcess ?? false : false}>
							<Stack direction="row" spacing={1} alignItems="center">
								<Text style={{ fontWeight: 600 }}>
									{failureDefinitionId ? failureDefinition.failureDefinition?.name ?? '-' : '-'}
								</Text>
								<EditIcon sx={{ fontSize: 'large', cursor: 'pointer' }} onClick={() => setOpen(true)} />
							</Stack>
						</MiniLoading>
					</StyledGridItem>
				</Grid>
			</Stack>
		</>
	);
};
